/**
 * Validation schemas for tasks, maintenance, communications, compliance and documents.
 */
import {
  ATTACHABLE_TYPES,
  COMMUNICATION_DIRECTIONS,
  COMMUNICATION_METHODS,
  DOCUMENT_CATEGORIES,
  MAINTENANCE_STATUSES,
  PRIORITIES,
  RECURRENCE_FREQUENCIES,
  TASK_STATUSES,
  values,
} from "../constants.ts";
import { v } from "../validation.ts";

const text = (max = 5000) => v.string({ max }).default("");

export const recurrenceSchema = v.object({
  frequency: v.enum(values(RECURRENCE_FREQUENCIES)),
  interval: v.int({ min: 1, max: 60 }).default(1),
  untilDate: v.date().optional(),
});

export const taskSchema = v.object({
  title: v.string({ min: 1, max: 200 }),
  description: text(),
  propertyId: v.uuid().optional(),
  tenancyId: v.uuid().optional(),
  assigneeId: v.uuid().optional(),
  dueDate: v.date().optional(),
  status: v.enum(values(TASK_STATUSES)).default("open"),
  priority: v.enum(values(PRIORITIES)).default("normal"),
  recurrence: v.optionalUnless(recurrenceSchema, "frequency"),
  reminderDays: v.array(v.int({ min: 0, max: 365 }), { max: 5 }),
  notes: text(),
});

export const maintenanceSchema = v.object({
  propertyId: v.uuid(),
  tenancyId: v.uuid().optional(),
  title: v.string({ min: 1, max: 200 }),
  description: text(),
  reportedDate: v.date(),
  reportedBy: text(200),
  priority: v.enum(values(PRIORITIES)).default("normal"),
  status: v.enum(values(MAINTENANCE_STATUSES)).default("reported"),
  contractorId: v.uuid().optional(),
  appointmentAt: v.datetime().optional(),
  quotedPence: v.pence({ min: 0, fromPounds: true }).optional(),
  costPence: v.pence({ min: 0, fromPounds: true }).optional(),
  completedDate: v.date().optional(),
  notes: text(),
});

export const maintenanceStatusSchema = v.object({
  status: v.enum(values(MAINTENANCE_STATUSES)),
  note: text(1000),
});

export const maintenanceCostSchema = v.object({
  amountPence: v.pence({ min: 1, fromPounds: true }),
  paidDate: v.date(),
  categoryId: v.uuid(),
  description: v.string({ min: 1, max: 500 }),
  reference: text(100),
});

export const communicationSchema = v.object({
  propertyId: v.uuid().optional(),
  tenancyId: v.uuid().optional(),
  tenantId: v.uuid().optional(),
  occurredAt: v.datetime(),
  method: v.enum(values(COMMUNICATION_METHODS)),
  direction: v.enum(values(COMMUNICATION_DIRECTIONS)),
  subject: v.string({ min: 1, max: 300 }),
  body: text(20000),
  followUpTitle: v.string({ max: 200 }).optional(),
  followUpDueDate: v.date().optional(),
});

export const complianceTypeSchema = v.object({
  name: v.string({ min: 1, max: 120 }),
  description: text(1000),
  validityMonths: v.int({ min: 1, max: 240 }).optional(),
  reminderDays: v.array(v.int({ min: 0, max: 730 }), { max: 6 }),
  isArchived: v.boolean(),
});

export const complianceItemSchema = v.object({
  propertyId: v.uuid(),
  typeId: v.uuid(),
  issueDate: v.date().optional(),
  expiryDate: v.date().optional(),
  reference: text(100),
  contractorId: v.uuid().optional(),
  notApplicable: v.boolean(),
  notes: text(),
});

export const complianceRenewSchema = v.object({
  issueDate: v.date(),
  expiryDate: v.date().optional(),
  reference: text(100),
  contractorId: v.uuid().optional(),
  notes: text(),
});

export const documentUpdateSchema = v.object({
  name: v.string({ min: 1, max: 255 }),
  category: v.enum(values(DOCUMENT_CATEGORIES)),
  propertyId: v.uuid().optional(),
  notes: text(),
});

export const documentLinkSchema = v.object({
  entityType: v.enum(ATTACHABLE_TYPES),
  entityId: v.uuid(),
});
